import i18next from 'i18next'
import React from 'react'
import {
  any,
  async,
  AsyncState,
  fromJSX,
  Fun,
  IOWidget,
  Updater
} from 'widgets-for-react'
import { scorePassword } from '../../../authentication/auth_widget/authenticationApi'
import { AuthState } from '../../../authentication/auth_widget/authState'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import { formatDate } from '../../../utils/date'
import { StudentSubscriptionLayout } from './studentSubscriptionLayout'
import {
  FormInput,
  StudentSubscriptionState,
  studentSubscriptionStateUpdaters
} from './studentSubscriptionState'

export const studentSubscriptionWidget =
  (
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>
  ): IOWidget<
    StudentSubscriptionState,
    Fun<
      StudentSubscriptionState,
      StudentSubscriptionState & { authStateUpdater?: Updater<AuthState> }
    >
  > =>
    (studentSubscriptionState) => {
      const schoolsState = studentSubscriptionState.schoolsState
      const checkCodeState = studentSubscriptionState.checkCodeState
      const checkEmailState = studentSubscriptionState.checkEmailState
      const subscribeExistingUserState =
        studentSubscriptionState.subscribeExistingUserState
      const subscribeNewUserState = studentSubscriptionState.subscribeNewUserState
      return any<Updater<StudentSubscriptionState>>([
        async(schoolsState).map(
          studentSubscriptionStateUpdaters.updateSchoolsState
        ),
        async(checkCodeState, undefined, { retry: { maxAttempts: 1 } }).map(
          studentSubscriptionStateUpdaters.updateCheckCodeState
        ),
        async(checkEmailState).map(
          studentSubscriptionStateUpdaters.updateCheckEmailState
        ),
        async(subscribeNewUserState).map(
          studentSubscriptionStateUpdaters.updateSubscribeNewUserState
        ),
        async(subscribeExistingUserState, undefined, { retry: { maxAttempts: 1 }}).map(
          studentSubscriptionStateUpdaters.updateSubscribeExistingUserState
        ),
        fromJSX((setState) => {
          if (studentSubscriptionState.trySubscribe
            && studentSubscriptionState.checkCodeState.kind == "loaded"
            && studentSubscriptionState.subscribeExistingUserState.kind == "unloaded") {
            setState(studentSubscriptionStateUpdaters.subscribeExistingUser)
          }

          const formInput = studentSubscriptionState.formInput
          const showFormInputValidation =
            studentSubscriptionState.showFormInputValidation

          const getOnChange =
            (field: keyof FormInput) => (newValue: string | boolean | number) =>
              setState(
                studentSubscriptionStateUpdaters.updateFormInput(field, newValue)
              )
          const onChange = {
            email: getOnChange('email'),
            schoolId: getOnChange('schoolId'),
            code: getOnChange('code'),
            name: getOnChange('name'),
            surname: getOnChange('surname'),
            graduationYear: getOnChange('graduationYear'),
            password: getOnChange('password'),
            showPassword: getOnChange('showPassword'),
            marketingEmails: getOnChange('marketingEmails'),
            termsAndConditions: getOnChange('termsAndConditions'),
          }

          const getOnBlur = (field: keyof FormInput) => () =>
            setState(
              studentSubscriptionStateUpdaters.showFormInputValition(field)
            )
          const onBlur = {
            email: getOnBlur('email'),
            schoolId: getOnBlur('schoolId'),
            code: getOnBlur('code'),
            name: getOnBlur('name'),
            surname: getOnBlur('surname'),
            graduationYear: getOnBlur('graduationYear'),
            password: getOnBlur('password'),
            showPassword: getOnBlur('showPassword'),
            marketingEmails: getOnBlur('marketingEmails'),
            termsAndConditions: getOnBlur('termsAndConditions'),
          }

          const isValid = {
            email: !!formInput.email && formInput.email.includes('@'),
            schoolId: !!formInput.schoolId,
            code: !!formInput.code,
            name: !!formInput.name,
            surname: !!formInput.surname,
            graduationYear: !!formInput.graduationYear,
            password: scorePassword(formInput.password || '').res === 'strong',
            showPassword: true,
            marketingEmails: true,
            termsAndConditions: formInput.termsAndConditions === true,
          }

          return (
            !isLoggedIn ? (
              <StudentSubscriptionLayout
                isLoggedIn={isLoggedIn}
                step="loginWall"
                onSubmit={() => { }}
                formInput={formInput}
                onChange={onChange}
                showFormInputValidation={showFormInputValidation}
                onBlur={onBlur}
                isValid={isValid}
              />
            ) :
              studentSubscriptionState.step == "enterSchool" ?
                (
                  <StudentSubscriptionLayout
                    isLoggedIn={isLoggedIn}
                    step="enterSchool"
                    schools={
                      schoolsState.kind === 'loaded'
                        ? schoolsState.value
                        : schoolsState.kind === 'loading'
                          ? 'loading'
                          : 'error'
                    }
                    onSubmit={() =>
                      setState(studentSubscriptionStateUpdaters.progressStateToCheckCode)
                    }
                    submitStatus={
                      checkCodeState.kind === 'unloaded' &&
                        checkEmailState.kind === 'unloaded'
                        ? 'not submitted'
                        : checkCodeState.kind === 'loading' ||
                          checkEmailState.kind === 'loading'
                          ? 'submitting'
                          : 'error'
                    }
                    formInput={formInput}
                    onChange={onChange}
                    showFormInputValidation={showFormInputValidation}
                    onBlur={onBlur}
                    isValid={isValid}
                  />
                )
                : (
                  <StudentSubscriptionLayout
                    isLoggedIn={isLoggedIn}
                    step="checkCode"
                    endDate={checkCodeState.kind == "loaded" && checkCodeState.value.kind == "found" && checkCodeState.value.data.length > 0 ?
                      formatDate.DDMonthYYYY(
                        i18next,
                        checkCodeState.value.data[0].EndDate
                      ) : undefined
                    }
                    onSubmit={() =>
                      setState(s => {
                        let s1 = studentSubscriptionStateUpdaters.checkCodeAndEmail(isLoggedIn)(s)
                        return studentSubscriptionStateUpdaters.flagToSubscribe(s1)
                      })
                    }
                    submitStatus={
                      checkCodeState.kind === 'unloaded'
                        ? 'not submitted'
                        : checkCodeState.kind === 'loading'
                          ? 'submitting'
                          : checkCodeState.kind === 'loaded' &&
                            (checkCodeState.value.kind === "invalid code" ||
                            checkCodeState.value.kind === 'found' && checkCodeState.value.data.length == 0)
                            ? 'invalid code'
                            : checkCodeState.kind === 'loaded' && checkCodeState.value.kind === 'too many attempts'
                              ? 'too many attempts'
                              : subscribeExistingUserState.kind === 'unloaded'
                                ? 'not submitted'
                                : subscribeExistingUserState.kind === 'loading' ||
                                  userSubscriptionsState.kind === 'loading'
                                  ? 'submitting'
                                  : subscribeExistingUserState.kind === 'loaded' &&
                                    userSubscriptionsState.kind === 'loaded'
                                    ? userSubscriptionsState.value.PaywallStatus ===
                                      'ValidStudentSubscriptionWithPausedSubscription'
                                      ? 'success and paused subscription'
                                      : userSubscriptionsState.value.PaywallStatus ===
                                        'ValidStudentSubscription'
                                        ? 'success'
                                        : 'error' 
                                    : 'error'
                    }
                    formInput={formInput}
                    onChange={onChange}
                    showFormInputValidation={showFormInputValidation}
                    onBlur={onBlur}
                    isValid={isValid}
                  />
                )
          )
        }),
      ])
    }
